import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

export interface PurchaseConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 구매할 상품명 (예: "랜덤 구슬 파이프 획득"). */
  readonly productName: string;
  /** 소모 재화 아이콘 키 (골드/다이아). */
  readonly costIconKey: string;
  /** 소모 재화 수량 표기 (formatBig 결과). */
  readonly costText: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const PANEL_W = 300;
const PANEL_H = 200;

/**
 * 구매 확인 팝업 — RebirthConfirmPopup 패턴(ModalLayer + NineSlicePanel + Yes/No).
 * 상점 재화 소비 구매(골드 1개/10개·다이아 상자) 시 오클릭 방지로 상품명 + 소모 재화를 표기하고
 * Yes(구매)에서만 onConfirm(실제 구매 command)을 호출한다. 본 팝업은 확인 UI만 — 결제 로직은 셸 책임(Humble View).
 */
export class PurchaseConfirmPopup extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: PurchaseConfirmPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - PANEL_H / 2;
    const dir = getTextDirection();
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, PANEL_H);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 24, t("shop.confirm.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, dir), top + 18, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());

    // 상품명 — 무엇을 사는지.
    const product = scene.add
      .text(cx, top + 78, opts.productName, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
        align: "center",
      })
      .setOrigin(0.5)
      .setWordWrapWidth(PANEL_W - 44);

    // 소모 재화 — 아이콘 + 수량 (중앙 정렬: 아이콘 좌, 수량 우).
    const costY = top + 116;
    const costAmount = scene.add
      .text(0, 0, opts.costText, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textBright,
      })
      .setOrigin(0, 0.5);
    const ICON = 16;
    const GAP = 4;
    const groupW = ICON + GAP + costAmount.width;
    const startX = cx - groupW / 2;
    const costIcon = scene.add
      .image(startX + ICON / 2, costY, opts.costIconKey)
      .setDisplaySize(ICON, ICON);
    costAmount.setPosition(startX + ICON + GAP, costY);
    this.contentContainer.add([title, closeBtn, product, costIcon, costAmount]);

    // Yes(구매) / No(취소) — onConfirm에서만 실제 구매.
    const yesBtn = new NineSliceButton(scene, cx - 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("shop.confirm.yes"),
      widthOverride: 108,
    });
    yesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onConfirm());
    const noBtn = new NineSliceButton(scene, cx + 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("shop.confirm.no"),
      widthOverride: 108,
    });
    noBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([yesBtn, noBtn]);
  }
}
